import type { CapturePhotoView } from '../../data/photos/photo-view';
import { usePhotoImageUrl } from '../photos/use-photo-image-url';
import { describeTile } from './report-wording';

/**
 * ONE CANDIDATE PHOTO IN THE REPORT BUILDER — M10 / S10-02.
 *
 * **A REAL CHECKBOX, FOR THE REASON THE CAPTURE ROW HAS ONE.** Choosing photos
 * is choosing items from a set, which is what `checked` means; `aria-pressed`
 * describes a toggle button and would be the wrong word for a screen reader.
 * The input is clipped rather than hidden with `display: none`, so it stays
 * focusable and announced, and **selection never depends on colour alone** —
 * the tick is the signal.
 *
 * **THE SMALL DERIVATIVE, NEVER THE ARCHIVE** (PT §9, which names report
 * selection explicitly: "lists, Capture viewing, report selection, PDFs — uses
 * the optimized variants"). A grid of full-resolution originals is how a phone
 * runs out of memory on a Project with real evidence.
 *
 * **INSPECT IS A SEPARATE CONTROL FROM CHOOSE, DELIBERATELY.** Brandon must
 * never be forced to decide from a tiny thumbnail (PT §17), so every tile
 * carries its own way into the full-screen viewer — and tapping to look must
 * not silently tick a box he has not decided about yet.
 */
export function ReportPhotoTile({
  view,
  selected,
  onToggle,
  onInspect,
}: {
  readonly view: CapturePhotoView;
  readonly selected: boolean;
  readonly onToggle: () => void;
  readonly onInspect: () => void;
}) {
  const url = usePhotoImageUrl(view, 'thumb');
  const description = describeTile(view);

  return (
    <li className="report-photo-tile">
      <label className={`report-photo-choose${selected ? ' report-photo-choose--on' : ''}`}>
        <input
          type="checkbox"
          className="report-capture-input"
          checked={selected}
          onChange={onToggle}
        />
        <span className="report-photo-image">
          {url ? (
            <img src={url} alt="" loading="lazy" />
          ) : (
            // A photo still arriving is not a broken photo, and says so.
            <span className="report-photo-placeholder" />
          )}
        </span>
        <span className="report-photo-box" aria-hidden="true">
          {selected ? '✓' : ''}
        </span>
        <span className="report-photo-name">{description}</span>
      </label>

      <button type="button" className="report-photo-inspect" onClick={onInspect}>
        Inspect
      </button>
    </li>
  );
}
